/**
 * Profile Processing Service - Convert collected scan data into detection profiles
 * Based on mobile app's ProfileProcessingService logic
 */

import { 
  ScanData, 
  DetectionProfile, 
  ReferenceCurve, 
  DetectionThresholds, 
  ProfileCategory,
  ProfileStats 
} from '../types/cardReaderProfiles';
import { Timestamp } from 'firebase/firestore';
import ProfileService from './ProfileService';

export class ProfileProcessingService {
  /**
   * Process collected scans into a complete detection profile
   */
  static async processTrainingScans(
    scans: ScanData[],
    profileName: string,
    category: ProfileCategory,
    masterOwnerID: string,
    createdBy: string
  ): Promise<DetectionProfile> {
    console.log(`🔄 Processing training session: ${profileName}`);
    console.log(`📊 Scans to process: ${scans.length}`);

    if (scans.length < 1) {
      throw new Error('At least 1 scan required for profile creation');
    }

    // Configuration matching mobile app defaults
    const config = {
      numCurves: Math.min(20, Math.max(5, Math.floor(scans.length / 3))), // Adaptive curve count
      pointsPerCurve: 200,
      cutoffMs: 800,
      activeCropThreshold: 0.1,
    };

    // Step 1: Process all scans to signatures
    console.log('📐 Step 1: Processing scans to signatures...');
    const signatures = scans.map((scan, index) => 
      this.processScanToSignature(scan, config.cutoffMs, config.activeCropThreshold, config.pointsPerCurve)
    );

    console.log(`✅ Processed ${signatures.length} signatures`);

    // Step 2: Select best representative curves
    console.log('🎯 Step 2: Selecting reference curves...');
    const referenceCurves = this.selectReferenceCurves(signatures, config.numCurves, scans);

    console.log(`✅ Selected ${referenceCurves.length} reference curves`);

    // Step 3: Compute training distances (all signatures to all references)
    console.log('📏 Step 3: Computing training distances...');
    const allDistances: number[] = [];

    for (const signature of signatures) {
      const distances = referenceCurves.map(ref =>
        this.computeDTWDistance(signature, ref.signature)
      );
      const minDistance = Math.min(...distances);
      allDistances.push(minDistance);
    }

    console.log(`✅ Computed ${allDistances.length} training distances`);

    // Step 4: Calculate thresholds from training distances
    console.log('🎚️ Step 4: Calculating thresholds...');
    const stats = this.calculateDistanceStats(allDistances);

    // Threshold calculation: mean + k*stdDev (k=2 is common)
    const k = 2.0;
    const primaryThreshold = stats.avg + k * stats.stdDev;
    const secondaryThreshold = stats.avg + (k * 0.7) * stats.stdDev; // 70% of primary

    const thresholds: DetectionThresholds = {
      primaryThreshold,
      secondaryThreshold,
      combinationLogic: 'OR',
      confidence: 0.85,
      optimized: false
    };

    console.log(`✅ Thresholds: primary=${primaryThreshold.toFixed(3)}, secondary=${secondaryThreshold.toFixed(3)}`);

    // Step 5: Create complete profile
    const now = Timestamp.now();
    const profileId = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const profile: DetectionProfile = {
      id: profileId,
      name: profileName,
      description: `FlowMatch-based detection profile created from ${scans.length} training scans`,
      category,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy,
      masterOwnerID,
      trainingSessionId: `session_${Date.now()}`,
      referenceCurves,
      thresholds,
      isActive: false,
      isShared: false,
      tags: ['training', 'flowmatch', category],
      stats: {
        totalScansProcessed: 0,
        cleanDetections: 0,
        skimmerDetections: 0,
        averageConfidence: 0,
        lastUsed: now,
        usageCount: 0,
        falsePositiveRate: 0,
        falseNegativeRate: 0,
        effectivenessScore: 75 // Initial score
      },
      processingParameters: {
        signatureLength: config.pointsPerCurve,
        samplingRate: 1000,
        filterParameters: {
          smoothingWindow: 5
        },
        normalizationMethod: 'z-score',
        activeCroppingEnabled: true
      }
    };

    // Step 6: Save to Firebase
    console.log('💾 Step 6: Saving profile to Firebase...');
    const savedProfile = await ProfileService.createProfile(profile, masterOwnerID);

    console.log(`✅ Profile created successfully: ${savedProfile.name}`);
    console.log(`📊 Final profile stats: ${referenceCurves.length} curves, ${scans.length} training scans`);

    return savedProfile;
  }

  /**
   * Process scan data to create normalized signature ready for DTW
   */
  private static processScanToSignature(
    scanData: ScanData,
    cutoffMs: number = 800,
    activeCropThreshold: number = 0.1,
    targetLength: number = 200
  ): number[] {
    // Use the sensor readings directly (already processed by device)
    let signature = [...scanData.sensorReadings];

    // Step 1: Limit to cutoff time window (approximate based on sample count)
    const maxSamples = Math.floor(cutoffMs); // Assume ~1ms per sample
    if (signature.length > maxSamples) {
      signature = signature.slice(0, maxSamples);
    }

    // Step 2: Apply active region cropping
    signature = this.applyActiveRegionCropping(signature, activeCropThreshold);

    // Step 3: Resample to target length
    signature = this.resampleSignature(signature, targetLength);

    // Step 4: Normalize with z-score
    signature = this.normalizeZScore(signature);

    return signature;
  }

  /**
   * Apply active region cropping to remove idle periods
   */
  private static applyActiveRegionCropping(signature: number[], threshold: number = 0.1): number[] {
    if (signature.length === 0) return signature;

    // Normalize signature to 0-1 range for cropping
    const min = Math.min(...signature);
    const max = Math.max(...signature);
    const range = max - min;

    if (range === 0) return signature;

    const normalized = signature.map(val => (val - min) / range);

    // Find start of active region
    let startIdx = 0;
    for (let i = 0; i < normalized.length; i++) {
      if (Math.abs(normalized[i]) > threshold) {
        startIdx = i;
        break;
      }
    }

    // Find end of active region
    let endIdx = normalized.length - 1;
    for (let i = normalized.length - 1; i >= 0; i--) {
      if (Math.abs(normalized[i]) > threshold) {
        endIdx = i;
        break;
      }
    }

    // Return cropped region from original signature
    return signature.slice(startIdx, endIdx + 1);
  }

  /**
   * Resample signature to target length using linear interpolation
   */
  private static resampleSignature(signature: number[], targetLength: number): number[] {
    if (signature.length === targetLength) return signature;
    if (signature.length === 0) return [];

    const resampled: number[] = [];
    const ratio = (signature.length - 1) / (targetLength - 1);

    for (let i = 0; i < targetLength; i++) {
      const index = i * ratio;
      const lowerIndex = Math.floor(index);
      const upperIndex = Math.ceil(index);

      if (lowerIndex === upperIndex || upperIndex >= signature.length) {
        resampled.push(signature[lowerIndex] || 0);
      } else {
        // Linear interpolation
        const fraction = index - lowerIndex;
        const interpolated =
          signature[lowerIndex] * (1 - fraction) + signature[upperIndex] * fraction;
        resampled.push(interpolated);
      }
    }

    return resampled;
  }

  /**
   * Apply Z-score normalization to signature
   */
  private static normalizeZScore(signature: number[]): number[] {
    if (signature.length === 0) return signature;

    // Calculate mean
    const mean = signature.reduce((sum, val) => sum + val, 0) / signature.length;

    // Calculate standard deviation
    const squaredDiffs = signature.map(val => Math.pow(val - mean, 2));
    const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / signature.length;
    const stdDev = Math.sqrt(variance);

    // Avoid division by zero
    if (stdDev === 0) {
      return signature.map(() => 0);
    }

    // Apply z-score normalization
    return signature.map(val => (val - mean) / stdDev);
  }

  /**
   * Compute DTW distance between two time series
   */
  private static computeDTWDistance(series1: number[], series2: number[]): number {
    const n = series1.length;
    const m = series2.length;

    // Create DTW matrix
    const dtw: number[][] = Array(n + 1)
      .fill(null)
      .map(() => Array(m + 1).fill(Infinity));

    dtw[0][0] = 0;

    // Fill DTW matrix
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const cost = Math.abs(series1[i - 1] - series2[j - 1]);
        dtw[i][j] = cost + Math.min(dtw[i - 1][j], dtw[i][j - 1], dtw[i - 1][j - 1]);
      }
    }

    return dtw[n][m];
  }

  /**
   * Select best representative curves from a set of signatures using diversity-based selection
   */
  private static selectReferenceCurves(
    signatures: number[][],
    numCurves: number,
    originalScans: ScanData[]
  ): ReferenceCurve[] {
    if (signatures.length <= numCurves) {
      return signatures.map((signature, index) => ({
        id: `curve_${index}`,
        signature,
        originalScanId: originalScans[index]?.scanId || `scan_${index}`,
        diversity: 1.0,
        weight: 1.0,
        metadata: {
          collectionDate: originalScans[index]?.timestamp || new Date(),
          deviceId: originalScans[index]?.deviceId || 'unknown'
        }
      }));
    }

    const selected: { signature: number[]; index: number }[] = [];
    const remaining = signatures.map((sig, idx) => ({ signature: sig, index: idx }));

    // Select first curve randomly
    const firstIdx = Math.floor(Math.random() * remaining.length);
    selected.push(remaining.splice(firstIdx, 1)[0]);

    // Greedily select curves that maximize minimum distance to selected curves
    while (selected.length < numCurves && remaining.length > 0) {
      let maxMinDistance = -1;
      let bestIdx = 0;

      for (let i = 0; i < remaining.length; i++) {
        const candidate = remaining[i];

        // Compute minimum distance to all selected curves
        const distances = selected.map(ref => this.computeDTWDistance(candidate.signature, ref.signature));
        const minDistance = Math.min(...distances);

        if (minDistance > maxMinDistance) {
          maxMinDistance = minDistance;
          bestIdx = i;
        }
      }

      selected.push(remaining.splice(bestIdx, 1)[0]);
    }

    // Convert to ReferenceCurve format
    return selected.map((item, curveIndex) => ({
      id: `curve_${curveIndex}`,
      signature: item.signature,
      originalScanId: originalScans[item.index]?.scanId || `scan_${item.index}`,
      diversity: 1.0,
      weight: 1.0,
      metadata: {
        collectionDate: originalScans[item.index]?.timestamp || new Date(),
        deviceId: originalScans[item.index]?.deviceId || 'unknown'
      }
    }));
  }

  /**
   * Calculate statistics from DTW distances
   */
  private static calculateDistanceStats(distances: number[]): {
    min: number;
    max: number;
    avg: number;
    stdDev: number;
  } {
    if (distances.length === 0) {
      return { min: 0, max: 0, avg: 0, stdDev: 0 };
    }

    const min = Math.min(...distances);
    const max = Math.max(...distances);
    const avg = distances.reduce((sum, d) => sum + d, 0) / distances.length;

    const squaredDiffs = distances.map(d => Math.pow(d - avg, 2));
    const variance = squaredDiffs.reduce((sum, d) => sum + d, 0) / distances.length;
    const stdDev = Math.sqrt(variance);

    return { min, max, avg, stdDev };
  }

  /**
   * Validate training session before processing
   */
  static validateTrainingScans(scans: ScanData[]): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check minimum scan count
    if (scans.length < 1) {
      errors.push(`Insufficient scans: ${scans.length} collected (minimum 1 required)`);
    }

    // Check all scans have valid data
    for (let i = 0; i < scans.length; i++) {
      const scan = scans[i];

      if (!scan.sensorReadings || scan.sensorReadings.length === 0) {
        errors.push(`Scan ${i + 1} has no sensor data`);
      }

      if (scan.sensorReadings && scan.sensorReadings.length < 50) {
        errors.push(`Scan ${i + 1} has insufficient data points (${scan.sensorReadings.length})`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}